import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * Desktop hover-preview for the tabbed product-list component.
 *
 * Wraps a `product-list` section (with `display: contents`, so layout is
 * unaffected). When the section's tabs link to collection pages, hovering a tab
 * on desktop previews that collection's products in the grid below, without
 * navigating. Clicking a tab still navigates to the collection page, and touch
 * devices are untouched.
 *
 * Data flow: tab href -> collection URL -> fetch the collection page -> extract
 * its `.resource-list__item` cards -> morph them into the live list (flicker-free;
 * already-loaded cards/images are preserved). Results are cached per collection so
 * repeat hovers are instant.
 *
 * `data-preview-view` selects an alternate collection template to fetch from
 * (`?view=<name>`). Without it we get the collection's OWN template, whose product
 * card is configured independently of the host grid's - e.g.
 * collection.single-skin-tone.json uses a deliberately smaller card (10px title and
 * price, a different stage block), so previewed cards would visibly differ from the
 * ones they replace. Pointing at a view whose card block mirrors the host's keeps
 * both renderings identical.
 *
 * When the host list is a carousel the items live inside `slideshow-slides`, one
 * `slideshow-slide` per item. In that case we swap the slides container's contents
 * (never the `slideshow-component` wrapper) and re-wrap each incoming item in a
 * clone of an existing slide, so the slideshow keeps its structure and slide sizing.
 *
 * @element ar-tab-hover-preview
 */
class ArTabHoverPreview extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #tabs = [];
  /** @type {HTMLElement | null} */
  #grid = null;
  /** @type {HTMLElement | null} */
  #tabsContainer = null;
  /**
   * The element whose children are swapped: the slides container for carousels,
   * otherwise the grid itself.
   * @type {HTMLElement | null}
   */
  #target = null;
  /**
   * An empty clone of an existing `slideshow-slide`, used to wrap incoming items.
   * Null for non-carousel layouts.
   * @type {HTMLElement | null}
   */
  #slideTemplate = null;
  /** @type {string} */
  #originalGridHTML = '';
  /** @type {number | undefined} */
  #hoverTimer;
  /** @type {string | null} */
  #activeHandle = null;
  /** @type {number} */
  #requestId = 0;
  /** @type {number} */
  #maxProducts = Infinity;
  /**
   * Cache of collection handle -> extracted grid items HTML (or a pending promise).
   * @type {Map<string, string | Promise<string>>}
   */
  #cache = new Map();

  connectedCallback() {
    // Desktop / fine-pointer only. Mobile & touch keep tap-to-navigate.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.#maxProducts = parseInt(this.dataset.maxProducts || '', 10) || Infinity;
    this.#grid = this.querySelector('.resource-list');

    const tabs = /** @type {HTMLAnchorElement[]} */ (
      Array.from(this.querySelectorAll('.collection-links .menu__item a[href*="/collections/"]'))
    ).filter((link) => this.#getHandle(link) != null);

    // No tabs or no grid: nothing to enhance.
    if (!this.#grid || tabs.length === 0) return;

    this.#tabs = tabs;
    this.#tabsContainer = tabs[0].closest('.collection-links') || this;

    // Scope this to the list's own carousel. Each product card contains a media
    // gallery that is itself a slideshow, so a loose `slideshow-slides` lookup can
    // land inside a card and inject the preview into a single card's gallery,
    // leaving the real slides untouched behind it.
    const slidesContainer = /** @type {HTMLElement | null} */ (
      this.#grid.querySelector(
        ':scope > * > slideshow-component > slideshow-container > slideshow-slides, :scope > slideshow-component > slideshow-container > slideshow-slides'
      )
    );
    const slideTemplate = slidesContainer?.querySelector(':scope > slideshow-slide');

    this.#target = slidesContainer || this.#grid;
    this.#slideTemplate = slideTemplate
      ? /** @type {HTMLElement} */ (slideTemplate.cloneNode(false))
      : null;
    this.#originalGridHTML = this.#target.innerHTML;

    for (const tab of this.#tabs) {
      tab.addEventListener('mouseenter', this.#handleTabEnter);
      tab.addEventListener('focus', this.#handleTabEnter);
    }
    this.#tabsContainer.addEventListener('mouseleave', this.#handleContainerLeave);
  }

  disconnectedCallback() {
    clearTimeout(this.#hoverTimer);
    for (const tab of this.#tabs) {
      tab.removeEventListener('mouseenter', this.#handleTabEnter);
      tab.removeEventListener('focus', this.#handleTabEnter);
    }
    this.#tabsContainer?.removeEventListener('mouseleave', this.#handleContainerLeave);
  }

  /**
   * @param {MouseEvent | FocusEvent} event
   */
  #handleTabEnter = (event) => {
    const tab = /** @type {HTMLAnchorElement} */ (event.currentTarget);
    const handle = this.#getHandle(tab);
    if (!handle) return;

    // Warm the cache immediately so the fetch overlaps the hover-intent delay.
    this.#fetchItems(handle);

    clearTimeout(this.#hoverTimer);
    // Hover-intent: wait briefly so a quick pass-through doesn't trigger a swap.
    this.#hoverTimer = window.setTimeout(() => this.#preview(handle, tab), 80);
  };

  #handleContainerLeave = () => {
    clearTimeout(this.#hoverTimer);
    this.#restore();
  };

  /**
   * Fetches (and caches) a collection's grid items as an HTML string.
   * @param {string} handle
   * @returns {Promise<string>}
   */
  #fetchItems(handle) {
    const cached = this.#cache.get(handle);
    if (cached != null) return Promise.resolve(cached);

    const view = this.dataset.previewView;
    const url = view
      ? `/collections/${handle}?view=${encodeURIComponent(view)}`
      : `/collections/${handle}`;

    const promise = fetch(url, { headers: { 'X-Requested-With': 'fetch' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed to load collection ${handle}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // A collection template can contain several product grids (the collection's
        // own products plus curated/upsell sections, some of which may be empty).
        // Pick the first grid that actually holds product cards, then keep only
        // populated items so we never inject empty placeholders.
        const grids = Array.from(doc.querySelectorAll('[data-testid="resource-list-grid"]'));
        const hasCard = (el) => el.querySelector('product-card, a[href*="/products/"]');
        const container = grids.find((grid) => hasCard(grid)) || grids[0] || doc;
        const itemsHTML = Array.from(container.querySelectorAll('.resource-list__item'))
          .filter((item) => hasCard(item))
          .map((item) => item.outerHTML)
          .join('');
        this.#cache.set(handle, itemsHTML);
        return itemsHTML;
      })
      .catch((error) => {
        this.#cache.delete(handle);
        throw error;
      });

    this.#cache.set(handle, promise);
    return promise;
  }

  /**
   * Fetches and displays the products for a collection.
   * @param {string} handle
   * @param {HTMLAnchorElement} tab
   */
  async #preview(handle, tab) {
    if (this.#activeHandle === handle) return;
    this.#activeHandle = handle;
    this.#setActiveTab(tab);

    const requestId = ++this.#requestId;

    let itemsHTML;
    try {
      itemsHTML = await this.#fetchItems(handle);
    } catch {
      return;
    }

    // A newer hover (or a restore) happened while fetching; discard this result.
    if (requestId !== this.#requestId || this.#activeHandle !== handle) return;
    if (!this.#grid || !itemsHTML) return;

    this.#swapGrid(itemsHTML);
  }

  #restore() {
    this.#requestId++;
    if (this.#activeHandle == null) return;
    this.#activeHandle = null;
    this.#clearActiveTab();
    this.#swapGrid(this.#originalGridHTML, true);
  }

  /**
   * Morphs new items into the live list, preserving the container's own attributes
   * (column CSS vars) and minimising DOM churn.
   * @param {string} itemsHTML - Extracted `.resource-list__item` markup.
   * @param {boolean} [preRendered] - True when the HTML is a saved snapshot of the
   *   container and therefore already in its final (slide-wrapped) shape.
   */
  #swapGrid(itemsHTML, preRendered = false) {
    const target = this.#target;
    if (!target) return;

    const next = /** @type {HTMLElement} */ (target.cloneNode(false));
    next.innerHTML = preRendered ? itemsHTML : this.#renderItems(itemsHTML);

    morph(target, next, MORPH_OPTIONS);

    // Morph is a best-effort reconciliation: when the old and new children share no
    // structure it can leave the previous subtree in place, which would render the
    // outgoing cards behind the incoming ones. Drop anything that is not one of the
    // elements we just asked for.
    const expected = this.#slideTemplate ? 'slideshow-slide' : '.resource-list__item';
    for (const child of Array.from(target.children)) {
      if (!child.matches(expected)) child.remove();
    }

    // A carousel's scroll position can point past the new (possibly shorter) set
    // of slides, which would leave the list looking empty.
    if (this.#slideTemplate) target.scrollLeft = 0;
  }

  /**
   * Trims the item list to the section's product limit and, for carousels, wraps
   * each item in a slide clone so the slideshow's sizing and refs keep working.
   * @param {string} itemsHTML
   * @returns {string}
   */
  #renderItems(itemsHTML) {
    const holder = document.createElement('div');
    holder.innerHTML = itemsHTML;

    let items = Array.from(holder.children);
    if (Number.isFinite(this.#maxProducts)) items = items.slice(0, this.#maxProducts);

    const template = this.#slideTemplate;
    if (!template) return items.map((item) => item.outerHTML).join('');

    return items
      .map((item, index) => {
        const slide = /** @type {HTMLElement} */ (template.cloneNode(false));
        slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
        slide.setAttribute(
          'style',
          (slide.getAttribute('style') || '').replace(
            /--slideshow-timeline:\s*--slide-\d+/,
            `--slideshow-timeline: --slide-${index}`
          )
        );
        slide.removeAttribute('slide-id');
        slide.innerHTML = item.outerHTML;
        return slide.outerHTML;
      })
      .join('');
  }

  /**
   * @param {HTMLAnchorElement} activeTab
   */
  #setActiveTab(activeTab) {
    for (const tab of this.#tabs) {
      const item = tab.closest('.menu__item') || tab;
      item.classList.toggle('is-previewing', tab === activeTab);
    }
  }

  #clearActiveTab() {
    for (const tab of this.#tabs) {
      const item = tab.closest('.menu__item') || tab;
      item.classList.remove('is-previewing');
    }
  }

  /**
   * Extracts a collection handle from a tab link's href.
   * @param {Element} link
   * @returns {string | null}
   */
  #getHandle(link) {
    const href = link.getAttribute('href');
    if (!href) return null;
    const match = href.match(/\/collections\/([^/?#]+)/);
    // Ignore "all products" and non-collection destinations.
    if (!match || match[1] === 'all') return null;
    return match[1];
  }
}

if (!customElements.get('ar-tab-hover-preview')) {
  customElements.define('ar-tab-hover-preview', ArTabHoverPreview);
}
